// Shared presentation rules for due-diligence queue items and mitigation entries — the approval
// dialog, queue card, and forester tab must render these identically.
import i18n from '@/i18n'
import { DueDiligenceApprovalStatus, RiskPresence, ConversionImpact, TractTypeCategory } from '@/utils/Enumerations.js'
import { speciesLayerEnabled, dueDiligenceApprovalEnabled } from '@/utils/dueDiligence/mode.js'

export function isApproved (queueItem) {
  return queueItem?.status === DueDiligenceApprovalStatus.Approved.value
}

// "Rejected" in the UI; Returned in the enum
export function isRejected (queueItem) {
  return queueItem?.status === DueDiligenceApprovalStatus.Returned.value
}

export function isDecided (queueItem) {
  return isApproved(queueItem) || isRejected(queueItem)
}

export function locationAndAcres (queueItem) {
  const parts = []
  const location = [queueItem.countrySecondarySubdivision, queueItem.countrySubdivision].filter(Boolean).join(', ')
  if (location) parts.push(location)
  if (queueItem.acres != null) parts.push(`${queueItem.acres} ${i18n.t('acres')}`)
  return parts.join(' · ')
}

export function harvestSummary (queueItem) {
  return [queueItem.harvestTypeName, queueItem.loggingMethodName].filter(Boolean).join(' · ')
}

// Conversion only exists on Delivered tracts; the tract form clears it for any other category
export function hasConversionField (queueItem) {
  return queueItem?.tractTypeCategory === TractTypeCategory.Delivered.value
}

let measuringContext = null

function measureText (text, font) {
  measuringContext = measuringContext ?? document.createElement('canvas').getContext('2d')
  measuringContext.font = font
  return measuringContext.measureText(text).width
}

// The widest CONVERSION eyebrow + chip among the cards, so every card reserves the same slot and the chips sit as
// close to HABITAT as the longest name allows. The metrics mirror .dd-eyebrow and Vuetify's small chip with an icon.
export function conversionSlotWidth (queueItems) {
  const chipTexts = (queueItems ?? []).filter(hasConversionField).map(item => item.conversion?.name ?? i18n.t('notSpecified'))
  if (chipTexts.length === 0) return null
  const fontFamily = window.getComputedStyle(document.querySelector('.v-application') ?? document.body).fontFamily
  const eyebrow = i18n.t('conversion').toUpperCase()
  const eyebrowWidth = measureText(eyebrow, `600 11px ${fontFamily}`) + eyebrow.length * 0.5
  const widestChipText = Math.max(...chipTexts.map(text => measureText(text, `12px ${fontFamily}`)))
  const chipPaddingIconAndBorder = 24 + 20 + 2
  const eyebrowGap = 8
  return Math.ceil(eyebrowWidth + eyebrowGap + widestChipText + chipPaddingIconAndBorder) + 4
}

export function conversionImpactStyle (impact) {
  switch (impact) {
    case ConversionImpact.Low.value: return { color: 'amber', textColor: 'white', outlined: false, icon: 'mdi-alert' }
    case ConversionImpact.Medium.value: return { color: 'warning', textColor: 'white', outlined: false, icon: 'mdi-alert' }
    case ConversionImpact.High.value: return { color: 'error', textColor: 'white', outlined: false, icon: 'mdi-alert' }
    default: return { color: 'grey darken-1', textColor: undefined, outlined: true, icon: null }
  }
}

// The lookup queries the scientific name or the common name (see natureServe.js), so offer it
// whenever the entry carries either one (primarily a manual mode phenomenon)
export function canCheckNatureServe (sp) {
  return !!(sp.commonName?.trim() || sp.scientificName?.trim())
}

export function presenceLabel (requiresHabitat) {
  return requiresHabitat ? i18n.t('habitat') : i18n.t('risk')
}

export function presenceHelp (requiresHabitat) {
  return requiresHabitat ? i18n.t('habitatPresenceHelp') : i18n.t('riskPresenceHelp')
}

// An exemption's behavior flags, rendered identically by the settings table and the tract picker.
// Auto-assignment is omitted outside the species layer — the flag is stored but nothing acts on it.
// Approval is omitted for the same reason when the workflow is off;
export function exemptionOptionChips (exemption, approvalRequirementActive = dueDiligenceApprovalEnabled()) {
  const chips = []
  if (exemption?.requiresApproval && approvalRequirementActive) chips.push({ key: 'requiresApproval', icon: 'mdi-clipboard-check-outline', color: 'primary', label: i18n.t('requiresApproval') })
  if (exemption?.autoAssignWhenNoHits && speciesLayerEnabled()) chips.push({ key: 'autoAssignWhenNoHits', icon: 'mdi-auto-fix', color: 'success', label: i18n.t('autoAssignWhenNoHits') })
  return chips
}

export function speciesCountLabel (count) {
  return `${count} ${count === 1 ? i18n.t('speciesHit') : i18n.t('speciesHits')}`
}

// Fill only the name fields the user left blank, so a lookup never overwrites typed input.
export function backfillSpeciesNames (row, result) {
  if (!row || !result) return
  if (!row.commonName.trim() && result.commonName) row.commonName = result.commonName
  if (!row.scientificName.trim() && result.scientificName) row.scientificName = result.scientificName
}

// null is "no filter applied" and stays distinct from an explicit NotSet.
export function riskPresenceFilterOptions () {
  return [
    { value: null, text: i18n.t('any'), icon: null },
    { value: RiskPresence.Present.value, text: i18n.t('present'), icon: 'mdi-alert' },
    { value: RiskPresence.NotPresent.value, text: i18n.t('notPresent'), icon: 'mdi-check' },
    { value: RiskPresence.NotSet.value, text: i18n.t('notSet'), icon: 'mdi-help-circle-outline' }
  ]
}

export const emptyDecidedFilter = () => ({
  selectedTeams: [],
  selectedMitigationGroups: [],
  riskPresence: null,
  status: null,
  resolvedFrom: null,
  resolvedTo: null
})
